import currency from 'currency.js';

import {
  DELIVERABLE_ADDITIONAL_COSTS,
  DELIVERABLE_TYPE_INSTAGRAM_POST,
  DELIVERABLE_TYPE_INSTAGRAM_REEL,
  DELIVERABLE_TYPE_INSTAGRAM_STORY,
  DELIVERABLE_TYPE_TIKTOK_VIDEO,
  DELIVERABLE_TYPE_YOUTUBE_VIDEO
} from 'utils/deliverables';
import {
  CHANNEL_TYPE_INSTAGRAM,
  CHANNEL_TYPE_TIKTOK,
  CHANNEL_TYPE_YOUTUBE
} from 'utils/channels';
import { getFormattedAmount } from 'utils/formatters';
import { renderPrice } from 'utils/helper/price';
import { __t } from 'utils/language';

import type { MetricsBarItem } from 'types';
import type { BudgetSplitResourceV2 } from 'modules/reporting/types/BudgetSplitResource.type';
import type { CustomReportOffer } from 'modules/campaigns/types/CampaignCustomReport.type';


export function prepareMetrics(
  splits: Array<BudgetSplitResourceV2>,
  groupedDeliverables: Array<Omit<CustomReportOffer, 'channels'>>,
  checkedIds: Array<number>
): Array<MetricsBarItem> {
  let totalCreatorsSelected = 0;

  const creativesAmount = {
    [DELIVERABLE_TYPE_YOUTUBE_VIDEO]: 0,
    [DELIVERABLE_TYPE_TIKTOK_VIDEO]: 0,
    [DELIVERABLE_TYPE_INSTAGRAM_POST]: 0,
    [DELIVERABLE_TYPE_INSTAGRAM_STORY]: 0,
    [DELIVERABLE_TYPE_INSTAGRAM_REEL]: 0
  };

  const channelFollowers = {
    [CHANNEL_TYPE_INSTAGRAM]: 0,
    [CHANNEL_TYPE_YOUTUBE]: 0,
    [CHANNEL_TYPE_TIKTOK]: 0
  };

  const channelBudget = {
    [CHANNEL_TYPE_INSTAGRAM]: 0,
    [CHANNEL_TYPE_YOUTUBE]: 0,
    [CHANNEL_TYPE_TIKTOK]: 0,
    [DELIVERABLE_ADDITIONAL_COSTS]: 0
  };

  const groupedSplits: {
    [x: number]: BudgetSplitResourceV2
  } = splits
    .reduce((res, item) => {
      res[item.creator.id] = item;
      return res;
    }, {});

  groupedDeliverables.forEach((offer) => {
    const selectedDeliverables = offer.deliverables
      .filter((deliverable) => deliverable.selectable && checkedIds.includes(deliverable.id));

    if (selectedDeliverables.length === 0) {
      return;
    }

    totalCreatorsSelected += 1;

    const creatorSelectedChannels = {
      [CHANNEL_TYPE_INSTAGRAM]: false,
      [CHANNEL_TYPE_YOUTUBE]: false,
      [CHANNEL_TYPE_TIKTOK]: false
    };

    selectedDeliverables.forEach((deliverable) => {
      Object.keys(creativesAmount).forEach((taskType) => {
        if (deliverable.deliverableType === taskType) {
          creativesAmount[taskType] += 1; // add task to total amount for this type

          // mark task channel as selected
          Object.keys(creatorSelectedChannels).forEach((channel) => {
            if (taskType.includes(channel)) {
              creatorSelectedChannels[channel] = true;
            }
          });
        }
      });
    });

    // calculate budgets
    const hasSplitDefined = !(groupedSplits[offer.id]?.budgetSplit.isOutdated ?? true);
    if (hasSplitDefined) {
      selectedDeliverables
        // To filter out additional content
        .filter(d => !d.additional)
        .forEach((deliverable) => {
          Object.keys(channelBudget).forEach((taskType) => {
            if (deliverable.deliverableType.includes(taskType)) {
              const taskSplit = groupedSplits[offer.id]
                ?.budgetSplit
                .taskSplits
                .find((split) => split.deliverableType === deliverable.deliverableType);

              if (!taskSplit) {
                return;
              }

              const budgetPerTask = taskSplit.budget / taskSplit.count;
              channelBudget[taskType] = channelBudget[taskType] + budgetPerTask;
            }
          });
        });

      const additionalCosts = groupedSplits[offer.id]
        ?.budgetSplit
        .additionalCosts ?? 0;
      channelBudget[DELIVERABLE_ADDITIONAL_COSTS] = channelBudget[DELIVERABLE_ADDITIONAL_COSTS] + additionalCosts;
    }

    // calculate followers if has any of the channels selected
    Object.keys(channelFollowers).forEach((channel) => {
      if (creatorSelectedChannels[channel]) {
        channelFollowers[channel] += groupedSplits[offer.id]
          ?.creator
          .metrics
          .find((item) => item.type === channel)
          ?.followers ?? 0;
      }
    });
  });

  const followersMetrics: Array<MetricsBarItem> = Object.keys(channelFollowers)
    .reduce((res: Array<MetricsBarItem>, channel) => {
      if (channelFollowers[channel]) {
        res.push({
          key: `${channel}-followers`,
          value: getFormattedAmount(channelFollowers[channel]),
          name: __t(`report_download_metrics_summary.${channel}_followers_label`)
        });
      }

      return res;
    }, []);

  const amountsMetrics: Array<MetricsBarItem> = Object.keys(creativesAmount)
    .reduce((res: Array<MetricsBarItem>, taskType) => {
      if (creativesAmount[taskType]) {
        res.push({
          key: `${taskType}-amount`,
          value: getFormattedAmount(creativesAmount[taskType]),
          name: __t(`report_download_metrics_summary.${taskType}_amount_label`, { withPlural: creativesAmount[taskType] })
        });
      }

      return res;
    }, []);

  const budgetMetrics: Array<MetricsBarItem> = Object.keys(channelBudget)
    .reduce((res: Array<MetricsBarItem>, taskType) => {
      if (channelBudget[taskType] > 0) {
        res.push({
          key: `${taskType}-budget`,
          value: renderPrice(currency(channelBudget[taskType]).value),
          name: __t(`report_download_metrics_summary.${taskType}_budget_label`)
        });
      }

      return res;
    }, []);

  return [
    {
      key: 'content-creators',
      value: totalCreatorsSelected,
      name: __t('report_download_metrics_summary.content_creator_label', { withPlural: totalCreatorsSelected })
    },
    ...followersMetrics,
    ...budgetMetrics,
    ...amountsMetrics
  ];
}
